/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

/* cookie banner content update custom js*/

document.addEventListener('DOMContentLoaded', function() {
  // Function to setup banner interactions
  function setupBanner(bannerDialog) {
    if (!bannerDialog) return;

    // Create and move policy links container
    const policyLink = bannerDialog.querySelector('#shopify-pc__banner__body-policy-link');
    const buttonsContainer = bannerDialog.querySelector('.shopify-pc__banner__btns');
    
    if (policyLink && buttonsContainer) {
      const policyDiv = document.createElement('div');
      policyDiv.className = 'shopify-pc__banner__policy-link-container';
      
      // Create terms and conditions link
      const termsLink = document.createElement('a');
      termsLink.href = '/pages/terms-and-conditions';
      termsLink.textContent = 'Terms & Conditions';
      termsLink.className = 'shopify-pc__banner__body-policy-link';
      termsLink.target = '_blank';
      termsLink.rel = 'noopener noreferrer';
      
      // Add both links to the container
      policyDiv.appendChild(termsLink);
      policyDiv.appendChild(policyLink);
      
      // Insert container after buttons
      buttonsContainer.parentNode.insertBefore(policyDiv, buttonsContainer.nextSibling);
    }

    // Add event listeners
    const acceptButton = bannerDialog.querySelector('#shopify-pc__banner__btn-accept');
    if (acceptButton) {
      acceptButton.addEventListener('click', function(e) {
        e.preventDefault();
        document.body.classList.remove('cookie-banner-visible');
      });
    }

    const rejectButton = bannerDialog.querySelector('#shopify-pc__banner__btn-decline');
    if (rejectButton) {
      rejectButton.addEventListener('click', function(e) {
        e.preventDefault();
        document.body.classList.remove('cookie-banner-visible');
      });
    }

    const bannerBody = bannerDialog.querySelector('.shopify-pc__banner__body');
    if (bannerBody) {
      document.body.classList.add('cookie-banner-visible');
    }
  }

  // Watch for banner insertion with optimized MutationObserver
  const observer = new MutationObserver(function(mutations) {
    mutations.forEach(function(mutation) {
      if (mutation.addedNodes.length) {
        const banner = document.getElementById('shopify-pc__banner');
        if (banner) {
          setupBanner(banner);
          observer.disconnect(); // Stop observing after we find the banner
        }
      }
    });
  });

  // Only observe body for added nodes
  observer.observe(document.body, { childList: true });
});

/* cookie banner content update custom js*/ 

// Add geolocation check for customers outside of Philippines
document.addEventListener('DOMContentLoaded', function() {
  // Check if notice was previously dismissed
  if (localStorage.getItem('outsidePhNoticeDismissed') === 'true') {
      return;
  } 

  // Get existing elements
  const overlay = document.querySelector('.geolocation-overlay');
  const modal = document.querySelector('.geolocation-modal');

  // Add event listeners if they haven't been added yet
  if (!modal.querySelector('.global-website-btn').hasAttribute('data-setup')) {
    modal.querySelector('.global-website-btn').addEventListener('click', () => {
      window.location.href = 'https://www.global.jdsports.com/';
      //window.open("https://www.global.jdsports.com/", "_blank");
    });
    modal.querySelector('.global-website-btn').setAttribute('data-setup', 'true');
  }

  if (!modal.querySelector('.stay-here-btn').hasAttribute('data-setup')) {
    modal.querySelector('.stay-here-btn').addEventListener('click', () => {
      overlay.classList.remove('active');
      modal.classList.remove('active');
      localStorage.setItem('outsidePhNoticeDismissed', 'true');
    });
    modal.querySelector('.stay-here-btn').setAttribute('data-setup', 'true');
  }

  /*if (!overlay.hasAttribute('data-setup')) {
      overlay.addEventListener('click', (e) => {
          if (e.target === overlay) {
              overlay.classList.remove('active');
              modal.classList.remove('active');
          }
      });
      overlay.setAttribute('data-setup', 'true');
  }*/

  // Helper: wait until window.Shopify and required fields are ready
  function waitForShopify(maxWaitMs = 7000, intervalMs = 100) {
    return new Promise((resolve) => {
      const start = Date.now();
      const check = () => {
        const ready = !!(
          window.Shopify &&
          typeof window.Shopify.country === 'string' &&
          window.Shopify.country.length > 0
        );
        if (ready) return resolve(true);
        if (Date.now() - start >= maxWaitMs) return resolve(false);
        setTimeout(check, intervalMs);
      };
      check();
    });
  }

  // Get browsing context suggestions to check if customer is outside PH
  waitForShopify().then((available) => {
    fetch(
      window.Shopify.routes.root
      + 'browsing_context_suggestions.json'
      + '?country[enabled]=true'
      + `&country[exclude]=${window.Shopify.country}`
      + '&language[enabled]=true'
      + `&language[exclude]=${window.Shopify.language}`,
      {
        headers: {
          'Accept': 'application/json'
        }
      }
    )
    .then(response => {
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      return response.json();
    })
    .then(data => {
      // If there are suggestions, it means the customer is outside PH
      if (data.detected_values.country.handle != 'PH') {
        overlay.classList.add('active');
        modal.classList.add('active');

        // Update country name in modal content
        const countryName = data.detected_values.country_name;
        const modalContent = modal.querySelector('.modal-content p');
        if (modalContent) {
          // Replace 'Country Origin' with the actual country name
          const text = modalContent.innerHTML;
          if (text.includes('(Country Origin)')) {
            modalContent.innerHTML = text.replace('(Country Origin)', countryName);
          }
        }
      }
    })
    .catch(() => {
      // If API fails, show modal as fallback
      overlay.classList.add('active');
      modal.classList.add('active');
    });
  });
});

// custom country popup js
document.addEventListener('DOMContentLoaded', () => {
    // Store DOM elements
    const trigger = document.querySelector('.store-finder-popup-trigger');
    const popupContainer = document.querySelector('.store-finder-popup-container');
    const popupContainerClose = document.querySelector('.popup-modal__toggle');
    const hasBackground = !popupContainer?.classList.contains('header');
    const popupContainerBg = hasBackground ? document.querySelector('.store-finder-popup-background') : null;

    // Toggle popup visibility
    const togglePopup = (show) => {
        if (!popupContainer) return;
        
        const action = show ? 'add' : 'remove';
        trigger?.classList[action]('active');
        popupContainer.classList[action]('active');
        if (hasBackground) {
            popupContainerBg?.classList[action]('active');
        }
    };

    // Handle click events
    const handleClick = (e) => {
        e.preventDefault();
        if (e.target.closest('.store-finder-popup-trigger')) {
            togglePopup(true);
        } else if (e.target.closest('.popup-modal__toggle') || 
            (hasBackground && e.target.closest('.store-finder-popup-background')) ||
            (!hasBackground && e.target.closest('.store-finder-popup-container'))) {
            togglePopup(false);
        }
    };

    // Add event listeners directly to specific elements
    if (trigger) {
        trigger.addEventListener('click', handleClick);
    }
    if (popupContainerClose) {
        popupContainerClose.addEventListener('click', handleClick);
    }
    if (hasBackground && popupContainerBg) {
        popupContainerBg.addEventListener('click', handleClick);
    }
}); 


// Inject OOS override styles globally (runs on all pages)
if (!document.getElementById('wishlist-oos-override-style')) {
  const style = document.createElement('style');
  style.id = 'wishlist-oos-override-style';
  style.textContent = `
    body button.swym-storefront-layout-grid-item-add-to-cart-button[disabled]:before {
      content: '' !important;
    }
    body button.swym-storefront-layout-grid-item-add-to-cart-button[disabled],
    #swym-storefront-layout-section-container button.swym-storefront-layout-grid-item-add-to-cart-button[disabled],
    .swym-storefront-layout-as-modal button.swym-storefront-layout-grid-item-add-to-cart-button[disabled] {
      color: #B5B4B0 !important;
      background: #F7F6F2 !important;
      border: none !important;
      cursor: not-allowed !important;
    }
    .swym-wishlist-item-hidden {
      display: none !important;
    }
    swym-storefront-layout-item[data-oos-hidden="true"] {
      display: none !important;
    }
  `;
  document.head.appendChild(style);
}

// Global OOS data management - persisted in localStorage
const WishlistOOS = {
  STORAGE_KEY: 'wishlist_oos_data',
  
  getData() {
    try {
      const data = localStorage.getItem(this.STORAGE_KEY);
      return data ? JSON.parse(data) : { oosHandles: [], oosTitles: [], totalOOS: 0 };
    } catch (e) {
      return { oosHandles: [], oosTitles: [], totalOOS: 0 };
    }
  },
  
  setData(data) {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
    } catch (e) {

    }
  },
  
  clear() {
    localStorage.removeItem(this.STORAGE_KEY);
  }
};

// Apply stored OOS count to header on ALL pages EXCEPT wishlist page
// Uses MutationObserver to re-apply adjustment when Swym updates the counter
(function applyStoredOOSToHeader() {
  if ((window.location.href).includes('/pages/swym-wishlist')) return;
  
  function adjustCounter(counter) {
    const storedData = WishlistOOS.getData();
    const oosCount = storedData.totalOOS || 0;
    
    // Get Swym's current count (the "real" total from Swym)
    const swymCount = parseInt(counter.dataset.count || counter.textContent) || 0;
    
    // Only adjust if there are OOS items to subtract
    if (oosCount > 0 && swymCount > 0) {
      const adjustedCount = Math.max(0, swymCount - oosCount);
      
      // Update display without triggering observer loop
      if (counter.textContent !== adjustedCount.toString()) {
        counter.textContent = adjustedCount.toString();
      }
      
      if (adjustedCount === 0) {
        counter.classList.remove('show-badge');
      } else {
        counter.classList.add('show-badge');
      }
    }
  }
  
  function setupCounterObserver(counter) {
    if (counter.dataset.oosObserverAttached) return;
    counter.dataset.oosObserverAttached = 'true';
    
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        // When Swym updates data-count attribute, re-apply the adjustment
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-count') {
          adjustCounter(counter);
        }
      });
    });
    
    observer.observe(counter, { 
      attributes: true, 
      attributeFilter: ['data-count'] 
    });
    
    // Initial adjustment
    adjustCounter(counter);
  }
  
  let attempts = 0;
  const maxAttempts = 30;
  
  const checkAndSetup = setInterval(() => {
    attempts++;
    const headerCounters = document.querySelectorAll('.swym-wishlist-header-counter');
    
    headerCounters.forEach(counter => {
      setupCounterObserver(counter);
    });
    
    if (attempts >= maxAttempts) {
      clearInterval(checkAndSetup);
    }
  }, 200);
})();

if ((window.location.href).includes('/pages/swym-wishlist')) { 
  // Clear stale OOS data on wishlist page load
  WishlistOOS.clear();
  
  setInterval(() => {
    const wishlitItemCount = document.querySelector('.swym-wishlist-header-counter');
    if (wishlitItemCount && parseInt(wishlitItemCount.dataset.count) > 0) {
      document.querySelector('#swym-wishlist-container-to-render')?.classList.add('active');
      document.querySelector('.wishlist-empty-wrapper')?.classList.remove('active');
    } else if (wishlitItemCount && parseInt(wishlitItemCount.dataset.count) === 0) {
      document.querySelector('#swym-wishlist-container-to-render')?.classList.remove('active');
      document.querySelector('#swym-wishlist-container-to-render')?.classList.add('empty');
      document.querySelector('.wishlist-empty-wrapper')?.classList.add('active');
    }
  }, 1000); 
  
  const checkedProducts = new Map();
  const oosProductTitles = new Set();
  let oosCheckRunning = false;
  
  async function isProductCompletelyOOS(productHandle) {
    if (checkedProducts.has(productHandle)) {
      return checkedProducts.get(productHandle);
    }
    
    try {
      const response = await fetch(`/products/${productHandle}.js`);
      if (!response.ok) {
        checkedProducts.set(productHandle, true);
        return true;
      }
      
      const product = await response.json();
      let isOOS = false;
      
      if (typeof product.available === 'boolean') {
        isOOS = product.available === false;
      } else {
        const hasAnyStock = product.variants?.some(v => v.available === true);
        isOOS = hasAnyStock === false;
      }
      
      if (isOOS && product.title) {
        oosProductTitles.add(product.title);
      }
      
      checkedProducts.set(productHandle, isOOS);
      return isOOS;
    } catch (e) {
      checkedProducts.set(productHandle, false);
      return false;
    }
  }
  
  async function hideOOSWishlistItems() {
    // Prevent multiple simultaneous runs
    if (oosCheckRunning) return;
    oosCheckRunning = true;
    
    // Get only VISIBLE items with actual product links (not in hidden containers)
    const wishlistItems = Array.from(document.querySelectorAll('.swym-storefront-layout-grid-item'))
      .filter(item => {
        // Must have a product link
        if (!item.querySelector('a[href*="/products/"]')) return false;
        // Must be visible (check offsetParent - null means hidden)
        if (item.offsetParent === null) return false;
        return true;
      });
    
    
    if (wishlistItems.length === 0) {
      oosCheckRunning = false;
      return;
    }
    
    let hiddenCount = 0;
    
    for (const item of wishlistItems) {
      // Skip if already processed in this session
      if (item.dataset.oosProcessed === 'true') {
        const parent = item.closest('swym-storefront-layout-item');
        if (parent?.style.display === 'none') hiddenCount++;
        continue;
      }
      
      const link = item.querySelector('a[href*="/products/"]');
      const href = link.getAttribute('href');
      const match = href.match(/\/products\/([^?#/]+)/);
      if (!match) continue;
      
      const productHandle = match[1];
      const isOOS = await isProductCompletelyOOS(productHandle);
      
      item.dataset.oosProcessed = 'true';
      
      if (isOOS) {
        const parentItem = item.closest('swym-storefront-layout-item');
        if (parentItem) {
          parentItem.style.display = 'none';
          parentItem.dataset.oosHidden = 'true';
        } else {
          item.style.display = 'none';
        }
        hiddenCount++;
      } else {
        console.log('[Wishlist OOS] Visible:', productHandle);
      }
    }
    
    // Save OOS data to localStorage for header count on other pages
    WishlistOOS.setData({
      oosHandles: [],
      oosTitles: Array.from(oosProductTitles),
      totalOOS: hiddenCount
    });
    
    // Update counts
    updateAllCounts(hiddenCount);
    
    oosCheckRunning = false;
  }
  
  function updateAllCounts(totalOOSHidden) {
    // Update collection preview thumbnails
    const collectionItems = document.querySelectorAll('.swym-storefront-layout-collection-grid-item');
    
    collectionItems.forEach(collection => {
      const imageContainer = collection.querySelector('.swym-storefront-layout-collection-grid-item-image-container');
      if (!imageContainer) return;
      
      const thumbnails = imageContainer.querySelectorAll('.swym-storefront-layout-collection-grid-item-primary-image, .swym-storefront-layout-collection-grid-item-secondary-image');
      let hiddenInCollection = 0;
      
      thumbnails.forEach(thumbnail => {
        const img = thumbnail.querySelector('img');
        if (!img) return;
        
        const altText = img.getAttribute('alt') || '';
        
        if (altText && oosProductTitles.has(altText)) {
          thumbnail.style.display = 'none';
          hiddenInCollection++;
        }
      });
      
      const countElement = collection.querySelector('.swym-storefront-layout-collection-grid-item-count');
      if (countElement && hiddenInCollection > 0) {
        if (!countElement.dataset.originalCount) {
          const match = countElement.textContent.match(/(\d+)/);
          if (match) {
            countElement.dataset.originalCount = match[1];
          }
        }
        
        const originalCount = parseInt(countElement.dataset.originalCount) || 0;
        const newCount = Math.max(0, originalCount - hiddenInCollection);
        countElement.textContent = `${newCount} item${newCount !== 1 ? 's' : ''}`;
      }
    });
    
    // Update "Other items in my Wishlist" count
    const hiddenOtherItems = document.querySelectorAll('swym-storefront-layout-item[data-oos-hidden="true"]').length;
    const otherItemsTitle = document.querySelector('.swym-storefront-layout-default-list-title');
    if (otherItemsTitle && hiddenOtherItems > 0) {
      if (!otherItemsTitle.dataset.originalText) {
        otherItemsTitle.dataset.originalText = otherItemsTitle.textContent;
      }
      
      const match = otherItemsTitle.dataset.originalText.match(/\((\d+)\)/);
      if (match) {
        const originalCount = parseInt(match[1]);
        const newCount = Math.max(0, originalCount - hiddenOtherItems);
        otherItemsTitle.textContent = otherItemsTitle.dataset.originalText.replace(/\(\d+\)/, `(${newCount})`);
      }
    }
    
    // Update header counter - always based on total wishlist minus OOS
    updateHeaderCounter(totalOOSHidden);
  }
  
  function updateHeaderCounter(totalOOSHidden) {
    const headerCounters = document.querySelectorAll('.swym-wishlist-header-counter');
    headerCounters.forEach(counter => {
      // Get original count from Swym (total wishlist items)
      if (!counter.dataset.oosOriginalCount) {
        const swymCount = parseInt(counter.dataset.count || counter.textContent) || 0;
        counter.dataset.oosOriginalCount = swymCount.toString();
      }
      
      const originalCount = parseInt(counter.dataset.oosOriginalCount) || 0;
      const newCount = Math.max(0, originalCount - totalOOSHidden);
      counter.textContent = newCount.toString();
      counter.dataset.count = newCount.toString();
      
      if (newCount === 0) {
        counter.classList.remove('show-badge');
      } else {
        counter.classList.add('show-badge');
      }
    });
  }
  
  function updateWishlistOOSButtons() {
    const wishlistButtons = document.querySelectorAll('.swym-storefront-layout-grid-item-add-to-cart-button');
    wishlistButtons.forEach(button => {
      if (button.disabled) {
        button.setAttribute('aria-label', 'Out of Stock');
        if (button.textContent.trim() !== 'Out of Stock') {
          button.textContent = 'Out of Stock';
        }
        button.style.cssText = 'color: #B5B4B0; background: #F7F6F2; border: none; cursor: not-allowed;';
      }
    });
  }
  
  // Wait for Swym to fully load before checking
  function waitForSwymAndRun() {
    const checkInterval = setInterval(() => {
      // Check for VISIBLE grid items with product links
      const visibleItems = Array.from(document.querySelectorAll('.swym-storefront-layout-grid-item'))
        .filter(item => item.querySelector('a[href*="/products/"]') && item.offsetParent !== null);
      
      if (visibleItems.length > 0) {
        clearInterval(checkInterval);
        
        // Small delay to ensure DOM is stable
        setTimeout(() => {
          updateWishlistOOSButtons();
          hideOOSWishlistItems();
        }, 300);
      }
    }, 500);
    
    // Stop checking after 15 seconds
    setTimeout(() => clearInterval(checkInterval), 15000);
  }
  
  // Start after DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', waitForSwymAndRun);
  } else {
    waitForSwymAndRun();
  }
  
  // Re-run OOS check when user navigates within wishlist (Swym uses hash for navigation)
  let lastHash = window.location.hash;
  setInterval(() => {
    if (window.location.hash !== lastHash) {
      lastHash = window.location.hash;
      oosCheckRunning = false;
      
      // Wait for Swym to load new content
      setTimeout(() => {
        hideOOSWishlistItems();
      }, 800);
    }
  }, 300);
}

if ((window.location.href).includes('/apps/parcelpanel')) {
  const getVisibleTrackingResult = () => {
    return [...document.querySelectorAll('.pp_tracking_result')].find(
      (result) => getComputedStyle(result).display !== 'none'
    );
  };

  const getParcelPanelTrackingDetails = (result) => {
    if (!result) {
      return {
        trackingNumber: '',
        deliveryMethod: 'Standard Shipping'
      };
    }

    const trackingLink = result.querySelector('.pp_tracking_carrier_bottom .pp-link-underline');
    const shippingMethodEl = result.querySelector(
      'li.PP-Section-ShippingMethod .pp_tracking_info, .PP-Section-ShippingMethod .pp_tracking_info'
    );

    return {
      trackingNumber: trackingLink?.textContent?.trim() || '',
      deliveryMethod: shippingMethodEl?.textContent?.trim() || 'Standard Shipping'
    };
  };

  const buildParcelPanelHeaderHtml = ({ trackingNumber, deliveryMethod }) => `
    <div class="pp_tracking_result_info_wrapper jd-pp-tracking-header">
      <div class="pp_tracking_result_info">
        <p class="pp_tracking_result_info_text">Thank you for your order!</p>
      </div>
      <div class="pp_tracking_order-date-info-wrapper">
        ${trackingNumber ? `<p class="pp_tracking_order_date_info_text tracking_numer_in_bar"><strong>Tracking No. </strong><br>${trackingNumber}</p>` : ''}
        <p class="pp_tracking_order_date_info_text tracking-method-pp"><strong>Delivery Method </strong><br>${deliveryMethod}</p>
      </div>
    </div>
  `;

  const placeParcelPanelHeader = (wrapper, trackingResult) => {
    if (!wrapper || !trackingResult || wrapper === trackingResult) return;

    if (wrapper.nextElementSibling !== trackingResult) {
      trackingResult.parentElement.insertBefore(wrapper, trackingResult);
    }

    wrapper.style.setProperty('display', window.innerWidth <= 768 ? 'block' : 'flex', 'important');
  };

  const updateParcelPanelHeader = (wrapper, details) => {
    if (!wrapper) return;

    const trackingEl = wrapper.querySelector('.tracking_numer_in_bar');
    const methodEl = wrapper.querySelector('.tracking-method-pp');

    wrapper.querySelector('.tracking--date-pp')?.closest('.pp_tracking_result_info_text')?.remove();

    if (methodEl) {
      methodEl.innerHTML = `<strong>Delivery Method </strong><br>${details.deliveryMethod}`;
    }

    if (trackingEl) {
      if (details.trackingNumber) {
        trackingEl.innerHTML = `<strong>Tracking No. </strong><br>${details.trackingNumber}`;
        trackingEl.style.display = '';
      } else {
        trackingEl.style.display = 'none';
      }
    } else if (details.trackingNumber) {
      const methodRow = wrapper.querySelector('.pp_tracking_order-date-info-wrapper');
      if (methodRow) {
        methodRow.insertAdjacentHTML(
          'afterbegin',
          `<p class="pp_tracking_order_date_info_text tracking_numer_in_bar"><strong>Tracking No. </strong><br>${details.trackingNumber}</p>`
        );
      }
    }

    placeParcelPanelHeader(wrapper, getVisibleTrackingResult());
  };

  if (window.innerWidth <= 768) {
    const parcePanel = setInterval(() => {
      const container = document.querySelector('.pp_tracking_form_in .pp_tracking_form_order');

      if (container && !container.parentElement.querySelector('.mobile_tracking_line')) {
        container.insertAdjacentHTML('afterend', `
          <div class="pp_tracking_line_center mobile_tracking_line">
            <div class="pp_tracking_word">Or</div>
          </div>
        `);
        clearInterval(parcePanel);
      }
    }, 1000);
  }

  setInterval(() => {
    const trackingResult = getVisibleTrackingResult();
    const orderTitle = document.querySelector('.pp_tracking_result_title');
    let headerWrapper = document.querySelector('.pp_tracking_result_info_wrapper');
    const details = getParcelPanelTrackingDetails(trackingResult);

    if (!orderTitle || !trackingResult) return;

    if (!headerWrapper) {
      trackingResult.insertAdjacentHTML('beforebegin', buildParcelPanelHeaderHtml(details));
      headerWrapper = document.querySelector('.pp_tracking_result_info_wrapper');
    }

    updateParcelPanelHeader(headerWrapper, details);
  }, 1000);
} 

// Disable wishlist button when OOS variant is selected
document.addEventListener('on:variant:change', (e) => {
  const wishlistBtn = document.getElementById('swym-atw-pdp-button') || document.getElementById('swym-atw-pdp-button-custom');
  if (!wishlistBtn) return;
  
  const variant = e.detail?.variant;
  const isAvailable = variant?.available;
  
  wishlistBtn.disabled = !isAvailable;
});

// Re-render the cart after EasyGift adds or removes gift items. EasyGift writes to the cart with
// its own requests, which the theme cannot see, so the drawer keeps stale contents until told.
(() => {
  let refreshTimer;

  // EasyGift can report several writes per interaction, so collapse them into one re-render.
  const requestCartRefresh = () => {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => {
      document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true, cancelable: false }));
    }, 150);
  };

  const subscribe = () => Boolean(
    window.EasyGift?.Events?.subscribe?.('afterCartRequest', requestCartRefresh)
  );

  if (subscribe()) return;

  let attempts = 0;
  const poll = setInterval(() => {
    if (subscribe() || (attempts += 1) > 40) clearInterval(poll);
  }, 250);
})();

// Hide quantity steppers, remove, and wishlist on EasyGift lines even if the
// product tag is missing or cased differently from `gwp`.
(() => {
  const isGiftLine = (item) => {
    const keys = Object.keys(item.properties || {}).map((key) => key.toLowerCase());
    return keys.includes('_gifted') || keys.includes('easygift_promo') || keys.some((key) => key.includes('easygift'));
  };

  const hideGiftControls = () => {
    fetch(`${window.Shopify?.routes?.root || '/'}cart.js`)
      .then((response) => (response.ok ? response.json() : null))
      .then((cart) => {
        if (!cart?.items) return;
        const giftVariantIds = new Set(
          cart.items.filter(isGiftLine).map((item) => String(item.id))
        );
        if (!giftVariantIds.size) return;

        document.querySelectorAll('.cart-item').forEach((row) => {
          const variantId = row.querySelector('[data-id]')?.getAttribute('data-id');
          if (!variantId || !giftVariantIds.has(String(variantId))) return;
          row.classList.add('cart-item--gwp');
          row.setAttribute('data-is-gwp', 'true');
        });
      })
      .catch(() => {});
  };

  document.addEventListener('on:cart:after-merge', hideGiftControls);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', hideGiftControls);
  } else {
    hideGiftControls();
  }
})();